/**
 * THE DICE, FOR THE TURN YOU ARE LOOKING AT.
 *
 * Howard, 16 Aug 2026: *"i thought that we had a die roller somewhere... Where
 * did it go and should we surface it?"*
 *
 * It never existed. N‑004 decided a physics dice tray — a pit‑box tool tray,
 * ribbed rubber, anodised lip, dice that skitter — and no line of it was ever
 * written. What the game HAS had all along is the ledger printing every die
 * after the fact, in its physical colour, buried in a feed you have to open. So
 * the dice were present and unfindable, which is indistinguishable from absent.
 *
 * This is the small honest version, and it is the one worth having first: the
 * RACE CAM read and every die the current turn actually threw, sitting in the
 * open, updating as the cursor moves. It rolls nothing — the race is already
 * resolved and the ledger is append‑only — so there is no way for it to change
 * a result. It is a WINDOW on the dice, not a tray.
 *
 * The physics tray remains N‑004 and remains unbuilt, and should stay that way
 * until the game is finished: craft on top of a thing that works is a good
 * trade, craft instead of a thing that works is not.
 */

import type { TurnResult } from '../domain/turn.ts';
import type { DiceRecord } from '../domain/ruleEvent.ts';
import { dieRoles } from './EventFeed.tsx';

export interface TurnDiceProps {
  turns: readonly TurnResult[];
  /** The turn being shown. 0 = the green flag, before anything was thrown. */
  cursor: number;
}

/**
 * Every die of a turn, in the order it was thrown.
 *
 * Flattened from the events rather than tracked separately, for the reason the
 * whole project keeps restating: a second record of something is a second
 * record that can drift. The events already carry the dice; this reads them.
 */
function diceOfTurn(turn: TurnResult | undefined): DiceRecord[] {
  if (!turn) return [];
  return turn.events.flatMap((e) => e.dice ?? []);
}

export default function TurnDice({ turns, cursor }: TurnDiceProps) {
  const turn = cursor > 0 ? turns[cursor - 1] : undefined;
  const cam = turn?.raceCam ?? null;
  const rolls = diceOfTurn(turn);

  return (
    <section className="dicepanel" aria-label="The dice, this turn">
      <header className="dicepanel__head">
        <span className="dicepanel__badge">On the table</span>
        {turn && <span className="dicepanel__turn">turn {turn.turn}</span>}
        <span className="dim dicepanel__count">
          {rolls.length === 0 ? 'no dice yet' : `${rolls.length} roll${rolls.length === 1 ? '' : 's'}`}
        </span>
      </header>

      {/* The RACE CAM is the turn's headline roll and gets its own line — black
          die names the group, the metallic pair reads the track card. */}
      {cam && (
        <div className="dicepanel__cam">
          <span className="die die--black">{cam.black}</span>
          <span className="die die--metallic">{cam.metallic[0]}</span>
          <span className="die die--metallic">{cam.metallic[1]}</span>
          <span className="dicepanel__read">
            {cam.total} · {cam.group}
          </span>
          <span className="dicepanel__result">{cam.printed}</span>
          {cam.overridden && (
            <span className="dicepanel__override" title={cam.footnote ?? ''}>
              footnote → {cam.result}
            </span>
          )}
        </div>
      )}

      <ol className="dicepanel__rolls">
        {rolls.map((roll, i) => {
          const roles = dieRoles(roll);
          return (
            <li key={i} className="dicepanel__roll">
              <span className="dicepanel__dice">
                {roll.dice.map((face, j) => (
                  <span key={j} className={`die die--${roles[j] ?? 'metallic'}`}>
                    {face}
                  </span>
                ))}
                {roll.total !== undefined && <span className="dicepanel__eq">{roll.total}</span>}
                {roll.code !== undefined && <span className="dicepanel__eq">{roll.code}</span>}
                {/* D-018 — a hand-entered roll must never look like a rolled one. */}
                {roll.entry && <span className="roll__entry">{roll.entry === 'hand' ? 'hand' : 'hand/engine'}</span>}
              </span>
              {/* Word-wrapped, not truncated. A label that says what the die was
                  FOR is the only reason this panel beats the raw numbers, and
                  clipping it would throw away the whole point. */}
              {roll.label && <span className="dicepanel__label">{roll.label}</span>}
            </li>
          );
        })}
        {rolls.length === 0 && (
          <li className="dicepanel__empty">
            {cursor === 0 ? 'Field is set. Nothing thrown yet.' : 'Nothing was thrown this turn.'}
          </li>
        )}
      </ol>
    </section>
  );
}
